// Secure Impersonation Service
// Replaces the old client-side impersonation with secure server-side validation

const SecureImpersonationService = {
  /**
   * Starts impersonation for the specified employee ID
   * @param {number} targetEmployeeId - The EP Employee ID to impersonate
   * @returns {Promise<Object>} - Response with impersonated user info
   */
  async startImpersonation(targetEmployeeId) {
    try {
      const response = await fetch('/api/impersonation/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({ targetEmployeeId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to start impersonation');
      }

      const data = await response.json();
      
      // Store impersonation context for UI display
      sessionStorage.setItem('impersonationContext', JSON.stringify({
        isImpersonating: true,
        impersonatedUser: data.impersonatedUser,
        startedAt: new Date().toISOString()
      }));

      return data;
    } catch (error) {
      console.error('Error starting impersonation:', error);
      throw error;
    }
  },

  /**
   * Stops the current impersonation session
   * @returns {Promise<Object>} - Response confirming impersonation stopped
   */
  async stopImpersonation() {
    try {
      const response = await fetch('/api/impersonation/stop', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to stop impersonation');
      }

      const data = await response.json();
      
      // Clear impersonation context
      sessionStorage.removeItem('impersonationContext');

      return data;
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      throw error;
    }
  },

  /**
   * Gets the current impersonation status from the server
   * @returns {Promise<Object>} - Current impersonation status
   */
  async getImpersonationStatus() {
    try {
      const response = await fetch('/api/impersonation/status', {
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get impersonation status');
      }

      const data = await response.json();
      
      // Update local storage to match server state
      if (data.isImpersonating) {
        sessionStorage.setItem('impersonationContext', JSON.stringify({
          isImpersonating: true,
          impersonatedUser: data.impersonatedUser,
          actualUser: data.actualUser
        }));
      } else {
        sessionStorage.removeItem('impersonationContext');
      }

      return data;
    } catch (error) {
      console.error('Error getting impersonation status:', error);
      // Clear local storage on error
      sessionStorage.removeItem('impersonationContext');
      return { isImpersonating: false };
    }
  },

  /**
   * Gets the local impersonation context (for UI display)
   * @returns {Object} - Local impersonation context
   */
  getLocalImpersonationContext() {
    const context = sessionStorage.getItem('impersonationContext');
    return context ? JSON.parse(context) : { isImpersonating: false };
  },

  /**
   * Gets the current authentication token
   * @returns {string} - Current auth token
   */
  getAuthToken() {
    const token = sessionStorage.getItem('accessToken');
    if (token) {
      // Remove surrounding quotes from the token
      return token.substring(1, token.length - 1);
    }
    return '';
  },

  /**
   * Clears all impersonation data (for logout scenarios)
   */
  clearImpersonationData() {
    sessionStorage.removeItem('impersonationContext');
  }
};

export default SecureImpersonationService;